import React, {forwardRef, useEffect, useState} from 'react';
import _ from 'lodash';

import K from '../k';

import ImagePlaceholder from '../assets/image-placeholder.png';

const ImageCard = forwardRef(function Component({
  src,
  placeholderSrc = ImagePlaceholder,
  alt,
  height,
  width,
  extra,
  extraStyles,
  style,
  wrapperStyle,
  onMouseEnter,
  onMouseLeave,
  disableHover, // hide extra and also don't assign classname=hoverable
  ...props
}, ref) {
  const [imgSrc, setImgSrc] = useState(null);
  const [isHovering, setIsHovering] = !_.isEmpty(extra) ? useState(false) : [false, null];
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const img = new Image();

    img.src = src;

    img.onload = () => {
      setImgSrc(src);
      setIsLoading(false);
    };
  }, [src]);

  const styles = {
    wrapper: {
      position: 'relative',
      ...wrapperStyle
    },
    extra: (sideKey) => {
      const extraStyle = _.get(extraStyles, sideKey, {});
      const sides = _.split(_.kebabCase(sideKey), '-'); //HINT sides will be like ['top', 'left'] so we can support topLeft, topRight, bottomLeft, bottomRight

      return ({
        position: 'absolute',
        zIndex: 2,
        [sides[0]]: K.spacing,
        [sides[1]]: K.spacing,
        ...(isHovering && !disableHover
          ? {pointerEvents: 'auto', visibility: 'visible'}
          : {pointerEvents: 'none', visibility: 'hidden'}
        ),
        ...extraStyle
      });
    },
    image: {
      display: 'block',
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
      transition: 'filter 0.5s',
      ...(isHovering && !disableHover
        ? {filter: 'brightness(80%)'}
        : {filter: 'none'}
      ),
      ...style
    },
    skeleton: {
      display: 'block',
      width: '100%',
      height: '100%',
      backgroundColor: K.colors.gray,
      background: 'linear-gradient(-90deg, #efefef 0%, #fcfcfc 50%, #efefef 100%)',
      backgroundSize: '400% 400%',
      animation: '{0% {background-position: 0% 0%;} 100% {background-position: -135% 0%;}} 1.2s ease-in-out infinite',
      ...style,
    },
  };

  const handleMouseEnter = (e) => {
    if (setIsHovering) setIsHovering(true);

    if (onMouseEnter) onMouseEnter(e);
  };

  const handleMouseLeave = (e) => {
    if (setIsHovering) setIsHovering(false);

    if (onMouseLeave) onMouseLeave(e);
  };

  return (
    <div
      style={styles.wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      {_.map(_.keys(extra), sideKey => (
        <div key={`image-extra-${sideKey}`} style={styles.extra(sideKey)}>{extra[sideKey]}</div>
      ))}
      {isLoading
        ? (<div
          className='pulse'
          style={styles.skeleton}
        />)
        : (<img
          ref={ref}
          {...{alt, height, width}}
          src={imgSrc}
          style={styles.image}
          {...props}
        />)}
    </div>
  );
});

export default ImageCard;
